/**
 * StructuredCarePlanEditor — P1 of the Care Plan v2 Structured Matrix
 * Upgrade.
 *
 * Only rendered when the `care_plan_v2_structured_matrix_enabled`
 * feature flag is ON. When the flag is OFF, `CarePlanTab` continues to
 * render the legacy `CarePlanForm`/`CarePlanDetail` flow and this
 * component is not mounted.
 *
 * Concerns owned by this component
 *   - Fetch the section catalog + the plan's `structured` envelope.
 *   - Drive the left-rail navigation and the per-section body.
 *   - Coordinate explicit Save-per-section + the dirty-state guard.
 *   - Surface readiness (completion%, missing-required-fields,
 *     HIGH-risk behaviour findings).
 *   - Drive the one-click migration dialog (preview / apply / undo).
 *   - Wire the AI Suggest button per section / row (mock-only).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Loader2, AlertTriangle, History, Wand2 } from 'lucide-react';
import { toast } from 'sonner';

import { SectionNav } from './SectionNav';
import { SectionBody } from './SectionBody';
import { CompletionRing } from './CompletionRing';
import { MigrationDialog } from './MigrationDialog';
import { HighRiskBehaviourWarning } from './HighRiskBehaviourWarning';
import { PdfPreviewSwitcher } from './PdfPreviewSwitcher';
import { VersionHistory } from './VersionHistory';
import { ApprovalReadinessPanel } from './ApprovalReadinessPanel';
import { RiskOverlayStrip } from './RiskOverlayStrip';
import { StartFromTemplateCard } from './StartFromTemplateCard';
import { EditSectionDialog } from './EditSectionDialog';

const STATUS_BADGE = {
  DRAFT: 'bg-amber-100 text-amber-800 border-amber-200',
  REVIEW: 'bg-blue-100 text-blue-800 border-blue-200',
  IN_REVIEW: 'bg-blue-100 text-blue-800 border-blue-200',
  APPROVED: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  ARCHIVED: 'bg-zinc-100 text-zinc-700 border-zinc-200',
};

export function StructuredCarePlanEditor({ planId, onClose, currentUser }) {
  const [catalog, setCatalog] = useState([]);
  const [planMeta, setPlanMeta] = useState(null);
  const [structured, setStructured] = useState(null);
  const [readiness, setReadiness] = useState(null);
  const [activeKey, setActiveKey] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dirtySections, setDirtySections] = useState({});
  const [migrationOpen, setMigrationOpen] = useState(false);
  const [versionsOpen, setVersionsOpen] = useState(false);
  const [editSectionKey, setEditSectionKey] = useState(null);

  const canMigrate = (currentUser?.permissions || []).includes('careplans.row.migrate');
  const canEdit = (currentUser?.permissions || []).includes('careplans.row.create');
  const canApprove = (currentUser?.permissions || []).includes('careplans.approve');
  // Section metadata editing uses the care-plan UPDATE permission (not row.create).
  const canEditMeta = (currentUser?.permissions || []).includes('careplans.create');
  const canEditDomain = (currentUser?.permissions || []).includes('careplans.domain.edit');

  const refreshPlan = useCallback(async () => {
    const [c, s, r] = await Promise.all([
      api.get('/careplans/section-catalog'),
      api.get(`/careplans/${planId}/structured`),
      api.get(`/careplans/${planId}/readiness`),
    ]);
    setCatalog(c.data.data || []);
    setPlanMeta({ status: s.data.data.status, plan_id: s.data.data.plan_id, participant_id: s.data.data.participant_id });
    setStructured(s.data.data.structured || { sections: [] });
    setReadiness(r.data.data);
    setLoading(false);
    if (!activeKey && c.data.data?.length) setActiveKey(c.data.data[0].key);
  }, [planId, activeKey]);

  useEffect(() => {
    refreshPlan().catch((e) => {
      toast.error('Failed to load structured care plan');
      // eslint-disable-next-line no-console
      console.error('[StructuredCarePlanEditor] load error', e);
    });
  }, [refreshPlan]);

  // Dirty-state guard
  useEffect(() => {
    const dirty = Object.values(dirtySections).some(Boolean);
    if (!dirty) return;
    const handler = (e) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirtySections]);

  const sectionByKey = useMemo(() => {
    const map = {};
    (structured?.sections || []).forEach((s) => { map[s.section_key] = s; });
    return map;
  }, [structured]);

  const onSaveSection = useCallback(async (sectionKey, payload) => {
    try {
      await api.put(`/careplans/${planId}/sections/${sectionKey}`, payload);
      setDirtySections((d) => ({ ...d, [sectionKey]: false }));
      toast.success('Section saved');
      await refreshPlan();
    } catch (e) {
      toast.error(e?.response?.data?.error?.message || 'Save failed');
    }
  }, [planId, refreshPlan]);

  const onSaveSectionMeta = useCallback(async (sectionKey, payload) => {
    try {
      await api.put(`/careplans/${planId}/sections/${sectionKey}/meta`, payload);
      toast.success('Section updated');
      setEditSectionKey(null);
      await refreshPlan();
    } catch (e) {
      toast.error(e?.response?.data?.error?.message || e?.response?.data?.detail || 'Section update failed');
    }
  }, [planId, refreshPlan]);

  const onRowCreate = useCallback(async (sectionKey, payload) => {
    try {
      await api.post(`/careplans/${planId}/sections/${sectionKey}/rows`, payload);
      await refreshPlan();
      toast.success('Row added');
    } catch (e) {
      toast.error(e?.response?.data?.error?.message || 'Create failed');
    }
  }, [planId, refreshPlan]);

  const onRowUpdate = useCallback(async (sectionKey, rowId, payload) => {
    try {
      await api.put(`/careplans/${planId}/sections/${sectionKey}/rows/${rowId}`, payload);
      await refreshPlan();
      toast.success('Row updated');
    } catch (e) {
      toast.error(e?.response?.data?.error?.message || 'Update failed');
    }
  }, [planId, refreshPlan]);

  const onRowDelete = useCallback(async (sectionKey, rowId) => {
    try {
      await api.delete(`/careplans/${planId}/sections/${sectionKey}/rows/${rowId}`);
      await refreshPlan();
      toast.success('Row deleted');
    } catch (e) {
      toast.error(e?.response?.data?.error?.message || 'Delete failed');
    }
  }, [planId, refreshPlan]);

  const onAiSuggest = useCallback(async ({ sectionKey, userBrief, row }) => {
    try {
      const body = {
        feature: 'care_plan_section',
        section_key: sectionKey,
        user_brief: userBrief || (row?.observation_or_need || ''),
        participant_ctx: { plan_id: planId, row_id: row?.id },
      };
      const r = await api.post('/ai-copilot/draft', body);
      return r.data?.data?.draft || null;
    } catch (e) {
      const msg = e?.response?.status === 404
        ? 'AI Copilot is currently disabled.'
        : (e?.response?.data?.error?.message || 'AI Suggest failed');
      toast.error(msg);
      return null;
    }
  }, [planId]);

  if (loading) {
    return (
      <Card data-testid="structured-careplan-loading">
        <CardContent className="p-8 flex items-center justify-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading structured care plan…
        </CardContent>
      </Card>
    );
  }

  const completionPct = readiness?.completion_pct ?? 0;
  const status = planMeta?.status || 'DRAFT';
  const activeSection = sectionByKey[activeKey];
  const activeCatalog = catalog.find((c) => c.key === activeKey);
  const dirty = Object.values(dirtySections).some(Boolean);

  return (
    <div className="space-y-4" data-testid="structured-careplan-editor">
      <Card>
        <CardContent className="p-4 flex flex-wrap items-center gap-3">
          <CompletionRing pct={completionPct} />
          <div className="flex-1 min-w-[220px]">
            <div className="flex items-center gap-2">
              <h3 className="font-semibold text-base">Structured Care Plan</h3>
              <Badge variant="outline" className={STATUS_BADGE[status] || ''} data-testid="structured-status-badge">
                {status}
              </Badge>
              <Badge variant="outline" className="bg-violet-50 text-violet-700 border-violet-200">
                {readiness?.row_count ?? 0} rows · {readiness?.section_count ?? 0} sections
              </Badge>
              {dirty && (
                <Badge variant="outline" className="bg-rose-50 text-rose-700 border-rose-200" data-testid="structured-dirty-indicator">
                  unsaved changes
                </Badge>
              )}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Each section is saved explicitly. Add, edit and review rows then click <strong>Save Section</strong>.
            </p>
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline"
                    onClick={() => setVersionsOpen(true)}
                    data-testid="structured-versions-open-button">
              <History className="h-3 w-3 mr-1" /> Versions
            </Button>
            {canMigrate && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => setMigrationOpen(true)}
                data-testid="structured-migration-open-button"
              >
                <Wand2 className="h-3 w-3 mr-1" /> Migrate from Legacy
              </Button>
            )}
            {onClose && (
              <Button size="sm" variant="ghost" onClick={onClose} data-testid="structured-close-button">
                Close
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      <RiskOverlayStrip planId={planId} />

      <ApprovalReadinessPanel
        readiness={readiness}
        planStatus={planMeta?.status}
        canApprove={canApprove}
        onSubmitForReview={async () => {
          try { await api.post(`/careplans/${planId}/submit-review`); toast.success('Submitted for review'); await refreshPlan(); }
          catch (e) { toast.error(e?.response?.data?.error?.message || 'Submit failed'); }
        }}
        onApprove={async () => {
          try { await api.post(`/careplans/${planId}/approve`); toast.success('Approved'); await refreshPlan(); }
          catch (e) {
            const err = e?.response?.data;
            const code = err?.detail?.code || err?.error?.code;
            if (code === 'HIGH_RISK_BEHAVIOUR_INCOMPLETE') {
              toast.error('Approval blocked — HIGH-risk behaviour rows are incomplete');
            } else { toast.error(err?.error?.message || err?.detail?.message || 'Approve failed'); }
          }
        }}
      />

      <PdfPreviewSwitcher planId={planId} />

      {(readiness?.high_risk_behaviour_findings || []).length > 0 && (
        <HighRiskBehaviourWarning findings={readiness.high_risk_behaviour_findings} />
      )}

      {(readiness?.missing_required_fields || []).length > 0 && (
        <Card data-testid="structured-missing-fields">
          <CardContent className="p-3 flex items-start gap-2 text-sm">
            <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5" />
            <div>
              <p className="font-medium text-amber-800">Missing required fields</p>
              <ul className="text-xs text-amber-700 mt-1 space-y-0.5">
                {readiness.missing_required_fields.slice(0, 6).map((m, i) => (
                  <li key={i}>
                    <code className="font-mono">{m.section_key}</code>
                    {m.row_id && <> · row {m.row_id}</>} · missing <code className="font-mono">{m.field}</code>
                  </li>
                ))}
                {readiness.missing_required_fields.length > 6 && (
                  <li>…and {readiness.missing_required_fields.length - 6} more</li>
                )}
              </ul>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-12 gap-4">
        <div className="col-span-4 lg:col-span-3">
          <SectionNav
            catalog={catalog}
            sectionByKey={sectionByKey}
            activeKey={activeKey}
            onSelect={setActiveKey}
            dirtySections={dirtySections}
            onEditSection={setEditSectionKey}
            canEditMeta={canEditMeta}
          />
        </div>
        <div className="col-span-8 lg:col-span-9 space-y-3">
          {(readiness?.row_count ?? 0) === 0 && (
            <StartFromTemplateCard
              participantId={planMeta?.participant_id}
              canCreate={canEdit}
              onCreated={(newPlanId) => {
                if (newPlanId) window.location.assign(`/participants/${planMeta?.participant_id || ''}`);
              }}
            />
          )}
          {activeSection || activeCatalog ? (
            <SectionBody
              catalog={activeCatalog}
              section={activeSection}
              canEdit={canEdit}
              onRowCreate={(p) => onRowCreate(activeKey, p)}
              onRowUpdate={(rid, p) => onRowUpdate(activeKey, rid, p)}
              onRowDelete={(rid) => onRowDelete(activeKey, rid)}
              onSaveSection={(payload) => onSaveSection(activeKey, payload)}
              markDirty={(v) => setDirtySections((d) => ({ ...d, [activeKey]: v }))}
              onAiSuggest={(payload) => onAiSuggest({ sectionKey: activeKey, ...payload })}
              planId={planId}
              participantId={planMeta?.participant_id}
              canEditDomain={canEditDomain}
            />
          ) : (
            <Card>
              <CardContent className="p-6 text-sm text-muted-foreground italic">
                Select a section on the left to begin.
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {migrationOpen && (
        <MigrationDialog
          planId={planId}
          onClose={() => setMigrationOpen(false)}
          onComplete={async () => { setMigrationOpen(false); await refreshPlan(); }}
        />
      )}

      {versionsOpen && (
        <VersionHistory
          planId={planId}
          onClose={() => setVersionsOpen(false)}
        />
      )}

      {editSectionKey && (
        <EditSectionDialog
          open={!!editSectionKey}
          onClose={() => setEditSectionKey(null)}
          section={sectionByKey[editSectionKey] || null}
          catalog={catalog.find((c) => c.key === editSectionKey) || null}
          onSave={(payload) => onSaveSectionMeta(editSectionKey, payload)}
        />
      )}
    </div>
  );
}

export default StructuredCarePlanEditor;
